import { type BottomTabBarProps } from 'expo-router/tabs';
import { useQuery } from '@apollo/client/react';
import { useRouter } from 'expo-router';
import { useState } from 'react';
import { StyleSheet, View } from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';

import { BottomTabBar } from '@/components/molecules/navigation/BottomTabBar';
import { ConfirmDialog } from '@/components/atoms/dialog';
import { StartRecordingNote } from '@/components/molecules/voice-recording';
import { GetHandoffUnreadStateDocument, MeDocument } from '@/generated/usermobile-bff/graphql';
import { useAiTranscriptionConsentDialog } from '@/hooks';
import { useTranslation } from '@/i18n';
import { GlobalRecordingBar } from '@/components/organisms/voice-recording/GlobalRecordingBar';
import { useVoiceRecordingStore, useRemoteConfigStore } from '@/stores';

export const AppBottomBar = ({ state, navigation }: BottomTabBarProps) => {
  const { t } = useTranslation();
  const router = useRouter();
  const insets = useSafeAreaInsets();
  const recording = useVoiceRecordingStore((state) => state.recording);
  const requestMicrophonePermission = useVoiceRecordingStore(
    (state) => state.requestMicrophonePermission,
  );
  const shouldConfirmLowStorageStart = useVoiceRecordingStore(
    (state) => state.shouldConfirmLowStorageStart,
  );
  const { data } = useQuery(MeDocument);
  const [showStartDialog, setShowStartDialog] = useState(false);
  const isAgentHandoffEnabled = useRemoteConfigStore((state) => state.isAgentHandoffEnabled);
  const { data: unreadStateData } = useQuery(GetHandoffUnreadStateDocument, {
    fetchPolicy: 'cache-and-network',
    skip: !isAgentHandoffEnabled,
  });
  const unreadHandoffCount = unreadStateData?.handoffUnreadState?.totalCount ?? 0;
  const [showLowStorageDialog, setShowLowStorageDialog] = useState(false);
  const { handleAccept, consentDialog, isConsentVisible } = useAiTranscriptionConsentDialog();

  const activeRouteName = state.routes[state.index].name;
  const isRecording = recording.status !== 'idle';

  const handleRecordPress = () => {
    if (!data?.me || isRecording) {
      return;
    }

    handleAccept(() => {
      setShowStartDialog(true);
    });
  };

  const proceedToRecording = async () => {
    if (!data?.me) {
      return;
    }

    const granted = await requestMicrophonePermission();
    if (!granted) {
      return;
    }

    router.push({
      pathname: '/recording',
      params: { userId: data.me.id, accountId: data.me.accountId },
    });
  };

  const handleConfirmStartRecording = async () => {
    if (!data?.me) {
      return;
    }

    setShowStartDialog(false);

    if (await shouldConfirmLowStorageStart()) {
      setShowLowStorageDialog(true);
      return;
    }

    await proceedToRecording();
  };

  const handleDismissStartRecording = () => {
    setShowStartDialog(false);
  };

  const handleConfirmLowStorageStart = async () => {
    setShowLowStorageDialog(false);

    await proceedToRecording();
  };

  const handleDismissLowStorageStart = () => {
    setShowLowStorageDialog(false);
  };

  const allItems = [
    {
      key: 'meetings',
      label: t('tabs.meetings'),
      iconName: 'account-multiple',
      isActive: activeRouteName === 'meetings',
      onPress: () => navigation.navigate('meetings'),
    },
    {
      key: 'ai-tasks',
      label: t('tabs.aiTasks'),
      iconName: 'message',
      isActive: activeRouteName === 'ai-tasks',
      badgeCount: unreadHandoffCount,
      onPress: () => navigation.navigate('ai-tasks'),
    },
    {
      key: 'record',
      label: t('tabs.startRecording'),
      iconName: 'microphone',
      isActive: false,
      isAction: true,
      onPress: handleRecordPress,
    },
    {
      key: 'settings',
      label: t('tabs.settings'),
      iconName: 'cog',
      isActive: activeRouteName === 'settings',
      onPress: () => navigation.navigate('settings'),
    },
  ];

  const items = isAgentHandoffEnabled
    ? allItems
    : allItems.filter((item) => item.key !== 'ai-tasks');

  const bottomPadding = insets.bottom;

  return (
    <View style={[styles.container, { paddingBottom: bottomPadding }]}>
      <GlobalRecordingBar />
      <BottomTabBar items={items} />
      {consentDialog}
      <ConfirmDialog
        visible={showStartDialog && !isConsentVisible}
        title={t('recording.startConfirmation.title')}
        body={t('recording.startConfirmation.body')}
        note={<StartRecordingNote />}
        confirmLabel={t('recording.startConfirmation.confirm')}
        dismissLabel={t('recording.startConfirmation.dismiss')}
        onConfirm={handleConfirmStartRecording}
        onDismiss={handleDismissStartRecording}
      />
      <ConfirmDialog
        visible={showLowStorageDialog}
        title={t('recording.lowStorageStart.title')}
        body={t('recording.lowStorageStart.body')}
        confirmLabel={t('recording.lowStorageStart.confirm')}
        dismissLabel={t('recording.lowStorageStart.dismiss')}
        onConfirm={handleConfirmLowStorageStart}
        onDismiss={handleDismissLowStorageStart}
      />
    </View>
  );
};

export const renderAppBottomBar = (props: BottomTabBarProps) => <AppBottomBar {...props} />;

const styles = StyleSheet.create({
  container: {
    width: '100%',
    backgroundColor: '#17344a',
  },
});
